import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, X } from "lucide-react";
import { toast } from "sonner";

export default function Cart() {
  const { cart, remove, refreshCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [coupon, setCoupon] = useState("");
  const [couponApplied, setCouponApplied] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { refreshCart(); }, [refreshCart]);

  const applyCoupon = async () => {
    try {
      const { data } = await api.post("/coupons/validate", { code: coupon });
      setCouponApplied(data);
      toast.success(`Coupon appliqué : -${data.percent}%`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Coupon invalide");
      setCouponApplied(null);
    }
  };

  const checkout = async () => {
    if (!user) { toast.error("Connectez-vous pour continuer"); navigate("/login?next=/panier"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        origin_url: window.location.origin,
        coupon_code: couponApplied?.code,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors du paiement");
      setLoading(false);
    }
  };

  const discount = couponApplied ? cart.subtotal * couponApplied.percent / 100 : 0;
  const total = cart.subtotal - discount;

  if (cart.items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center fade-in">
        <h1 className="font-serif text-4xl md:text-5xl tracking-tight">Votre panier est vide</h1>
        <p className="mt-4 text-muted-foreground">Découvrez notre catalogue et ajoutez votre premier livre.</p>
        <Link to="/catalogue">
          <Button data-testid="cart-empty-catalog-btn" className="mt-8 rounded-sm">Voir le catalogue</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 fade-in">
      <h1 className="font-serif text-5xl tracking-tight mb-12">Panier</h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-4">
          {cart.items.map((it) => (
            <div key={it.book.id} data-testid={`cart-item-${it.book.id}`} className="flex gap-4 p-4 border border-border">
              <img src={it.book.cover_url} alt={it.book.title} className="w-20 h-28 object-cover border border-border" />
              <div className="flex-1">
                <div className="label-eyebrow text-muted-foreground">{it.book.category}</div>
                <Link to={`/livre/${it.book.id}`} className="font-serif text-xl hover:text-accent">{it.book.title}</Link>
                <div className="text-sm text-muted-foreground mt-1">{it.book.author}</div>
                <div className="mt-2 text-sm">Quantité : {it.quantity}</div>
              </div>
              <div className="flex flex-col justify-between items-end">
                <button data-testid={`cart-remove-${it.book.id}`} onClick={() => remove(it.book.id)} className="text-muted-foreground hover:text-destructive">
                  <Trash2 className="w-4 h-4" />
                </button>
                <div className="font-serif text-xl">{it.line_total.toFixed(2).replace(".", ",")} €</div>
              </div>
            </div>
          ))}
        </div>
        <div className="lg:col-span-1">
          <div className="p-6 border border-border sticky top-24">
            <div className="label-eyebrow mb-4">Récapitulatif</div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span>Sous-total</span><span>{cart.subtotal.toFixed(2).replace(".", ",")} €</span></div>
              {couponApplied && (
                <div className="flex justify-between text-accent">
                  <span className="flex items-center gap-2">
                    Coupon {couponApplied.code}
                    <button onClick={() => setCouponApplied(null)}><X className="w-3 h-3" /></button>
                  </span>
                  <span>-{discount.toFixed(2).replace(".", ",")} €</span>
                </div>
              )}
              <div className="flex justify-between pt-4 border-t border-border font-serif text-xl">
                <span>Total</span><span>{total.toFixed(2).replace(".", ",")} €</span>
              </div>
            </div>

            <div className="mt-6 space-y-2">
              <div className="flex gap-2">
                <Input data-testid="coupon-input" value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="Code promo" className="rounded-sm bg-transparent" />
                <Button data-testid="coupon-apply" onClick={applyCoupon} variant="outline" className="rounded-sm">Appliquer</Button>
              </div>
              <p className="text-xs text-muted-foreground">Essayez : BIENVENUE10</p>
            </div>

            <Button data-testid="checkout-btn" onClick={checkout} disabled={loading} size="lg" className="w-full mt-6 rounded-sm h-12">
              {loading ? "Redirection..." : "Passer au paiement"}
            </Button>
            <p className="text-xs text-muted-foreground mt-4 text-center">Paiement sécurisé via Stripe</p>
          </div>
        </div>
      </div>
    </div>
  );
}
